import { useState, useEffect, useCallback } from "react";
import { getAdminUsers } from "@/lib/api";
import { AdminUserRow } from "@/components/admin/AdminUserRow";
import { Loader2, Search, ChevronUp, ChevronDown } from "lucide-react";
import { logWarn } from "@/lib/log";

const COLUMNS = [
    { id: "email", label: "User", span: "col-span-4" },
    { id: "credits", label: "Balance", span: "col-span-2 text-right" },
    { id: "credits_used", label: "Used", span: "col-span-2 text-right" },
    { id: "created_at", label: "Joined", span: "col-span-2 text-right" },
];

const UsersTableHeader = ({ sort, order, onSort }) => (
    <div className="grid grid-cols-12 px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
        {COLUMNS.map((c) => (
            <button
                key={c.id}
                onClick={() => onSort(c.id)}
                className={`${c.span} flex items-center gap-1 hover:text-white transition-colors uppercase tracking-[0.25em] ${
                    c.span.includes("right") ? "justify-end" : ""
                } ${sort === c.id ? "text-blaze" : ""}`}
                data-testid={`admin-sort-${c.id}`}
            >
                {c.label}
                {sort === c.id &&
                    (order === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
            </button>
        ))}
        <div className="col-span-2 text-right">Actions</div>
    </div>
);

const UsersPagination = ({ data, page, setPage }) => (
    <div className="flex items-center justify-between mt-4 font-mono text-[11px] text-white/50" data-testid="admin-users-pagination">
        <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page <= 1}
            className="px-4 py-2 border border-white/15 uppercase tracking-[0.2em] hover:border-white/50 disabled:opacity-30 transition-colors"
            data-testid="admin-users-prev"
        >
            Prev
        </button>
        <span>
            Page {data.page} / {data.pages} · {data.total.toLocaleString()} users
        </span>
        <button
            onClick={() => setPage((p) => Math.min(p + 1, data.pages))}
            disabled={page >= data.pages}
            className="px-4 py-2 border border-white/15 uppercase tracking-[0.2em] hover:border-white/50 disabled:opacity-30 transition-colors"
            data-testid="admin-users-next"
        >
            Next
        </button>
    </div>
);

const UsersTableBody = ({ data, onChanged }) => {
    if (data === null) {
        return (
            <div className="p-10 flex justify-center">
                <Loader2 className="w-5 h-5 text-blaze animate-spin" />
            </div>
        );
    }
    if (data.users.length === 0) {
        return (
            <div className="p-10 font-mono text-xs text-white/30 text-center" data-testid="admin-users-empty">
                No users match this search.
            </div>
        );
    }
    return data.users.map((u) => <AdminUserRow key={u.user_id} u={u} onChanged={onChanged} />);
};

const UserSearchBox = ({ value, onChange }) => (
    <div className="flex items-center gap-2 border border-white/15 bg-obsidian px-3 mb-4 max-w-md focus-within:border-electric transition-colors">
        <Search className="w-3.5 h-3.5 text-white/40 shrink-0" />
        <input
            value={value}
            onChange={onChange}
            placeholder="Search by email or name..."
            className="flex-1 bg-transparent py-2.5 font-mono text-xs text-white/80 outline-none placeholder:text-white/25"
            data-testid="admin-user-search"
        />
    </div>
);

export const AdminUsersTable = () => {
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);

    const load = useCallback(() => {
        getAdminUsers({ search, sort, order, page, limit: 20 })
            .then(setData)
            .catch((e) => logWarn("admin users load failed", e));
    }, [search, sort, order, page]);

    useEffect(() => {
        const t = setTimeout(load, search ? 350 : 0);
        return () => clearTimeout(t);
    }, [load, search]);

    const toggleSort = (col) => {
        if (sort === col) setOrder((o) => (o === "asc" ? "desc" : "asc"));
        else {
            setSort(col);
            setOrder(col === "email" ? "asc" : "desc");
        }
        setPage(1);
    };

    return (
        <div data-testid="admin-users-panel">
            <UserSearchBox
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                }}
            />

            <div className="border border-white/10 bg-surface">
                <UsersTableHeader sort={sort} order={order} onSort={toggleSort} />
                <UsersTableBody data={data} onChanged={load} />
            </div>

            {data && data.pages > 1 && <UsersPagination data={data} page={page} setPage={setPage} />}
        </div>
    );
};
